import { useEffect, useState } from 'react';
import {
  SelectPeopleModal,
  type SelectPeopleSection,
  type SelectPersonRow,
} from 'src/design-system/SelectPeopleModal';
import { FamilyMember } from 'src/non-financial-data/v2/family/useFamilyMembers';

export type OwnerSelection = { client_id: string } | { external_name: string };

type FamilyOwnerModalProps = {
  opened: boolean;
  onClose: () => void;
  members: FamilyMember[];
  currentClientId: string;
  /** Members ticked when the picker opens. Defaults to the client the flow is anchored on. */
  initialClientIds?: string[];
  onContinue: (owners: OwnerSelection[]) => void;
};

type ExternalOwner = { id: string; name: string };

export const FamilyOwnerModal = ({
  opened,
  onClose,
  members,
  currentClientId,
  initialClientIds,
  onContinue,
}: FamilyOwnerModalProps) => {
  // Opening resets the picker to its preselection. Callers rebuild that array on every render,
  // so the reset keys off its contents rather than its identity.
  const preselectedKey = (initialClientIds ?? [currentClientId]).join(',');
  const preselectedIds = () => (preselectedKey === '' ? [] : preselectedKey.split(','));
  const [selectedClientIds, setSelectedClientIds] = useState<string[]>(preselectedIds);
  const [externals, setExternals] = useState<ExternalOwner[]>([]);
  const [seq, setSeq] = useState(0);

  useEffect(() => {
    if (!opened) return;
    setSelectedClientIds(preselectedKey === '' ? [] : preselectedKey.split(','));
    setExternals([]);
    setSeq(0);
  }, [opened, preselectedKey]);

  const toggleClient = (clientId: string) =>
    setSelectedClientIds(previous =>
      previous.includes(clientId)
        ? previous.filter(id => id !== clientId)
        : [...previous, clientId],
    );

  const memberRows: SelectPersonRow[] = members.map(member => ({
    id: member.clientId,
    name: `${member.client.firstName} ${member.client.lastName}`,
    role: member.isMain ? 'Main' : '',
    checked: selectedClientIds.includes(member.clientId),
    onToggle: () => toggleClient(member.clientId),
  }));

  const externalRows: SelectPersonRow[] = externals.map(external => ({
    id: external.id,
    name: external.name,
    role: 'External',
    checked: true,
    onToggle: () => setExternals(previous => previous.filter(item => item.id !== external.id)),
  }));

  const section: SelectPeopleSection = {
    key: 'owners',
    title: 'Owners',
    addLabel: 'Add external owner',
    searchPlaceholder: 'External owner name',
    noneLabel: 'Add an external owner',
    allowFreeText: true,
    rows: [...memberRows, ...externalRows],
    getResults: () => [],
    onAddFreeText: typed => {
      setExternals(previous => [...previous, { id: `external:${seq}`, name: typed }]);
      setSeq(previous => previous + 1);
    },
  };

  const handleConfirm = () => {
    const owners: OwnerSelection[] = [
      ...selectedClientIds.map(id => ({ client_id: id })),
      ...externals.map(external => ({ external_name: external.name })),
    ];
    onContinue(owners);
  };

  return (
    <SelectPeopleModal
      opened={opened}
      onClose={onClose}
      title="Select family owners of this asset"
      subtitle="You can also add external owners here"
      confirmLabel="Continue"
      confirmDisabled={selectedClientIds.length === 0 && externals.length === 0}
      sections={[section]}
      onConfirm={handleConfirm}
    />
  );
};
